import type { UserStatus } from "@/api-schema-enums";
import { useAbility } from "@/casl-provider";
import { getFullUserName } from "@/common/helpers/get-full-user-name";
import { canViewUserDetail, getUserDetailPath } from "@/common/helpers/get-user-detail-path";
import { isUserDeactivated } from "@/common/helpers/user-deactivated";
import { cn } from "@/common/helpers/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { UserAvatar, UserAvatarSize } from "@/components/user-avatar/user-avatar";
import { userStore } from "@/store/user-store";
import { ArrowUpRightIcon } from "lucide-react";
import type React from "react";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";
import { UserName } from "./user-name";

export interface UserItemUser {
  uuid: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  azureObjectUuid?: string | null;
  status?: UserStatus | null;
}

export enum UserItemVariant {
  Default = "Default",
  Compact = "Compact",
}

export interface UserItemProps {
  user: UserItemUser;
  showEmail?: boolean;
  highlighted?: boolean;
  badge?: React.ReactNode;
  endContent?: React.ReactNode;
  tooltip?: string;
  disableLink?: boolean;
  variant?: UserItemVariant;
  className?: string;
}

export function UserItem({
  user,
  showEmail,
  highlighted,
  badge,
  endContent,
  tooltip,
  disableLink = false,
  variant = UserItemVariant.Default,
  className,
}: UserItemProps) {
  const { t } = useTranslation("common");
  const ability = useAbility();
  const viewerRoles = userStore((state) => state.user?.roles);

  const isLinked = !disableLink && canViewUserDetail(ability, viewerRoles);
  const isCompact = variant === UserItemVariant.Compact;
  const isDeactivated = isUserDeactivated(user);
  const avatarName = getFullUserName({ user, emailFallback: false, fallback: "" }) || undefined;
  const nameTooltip = tooltip ?? (isLinked ? t("ViewProfile") : undefined);
  const tooltipDescriptionId = useId();
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [hasKeyboardFocus, setHasKeyboardFocus] = useState(false);

  const avatar = (
    <UserAvatar
      userAzureObjectUuid={user.azureObjectUuid}
      name={avatarName}
      size={isCompact ? UserAvatarSize.Xs : UserAvatarSize.Md}
      isDeactivated={isDeactivated}
    />
  );

  const name = (
    <span className="inline-flex min-w-0 items-center gap-1">
      <UserName user={user} className={cn("text-sm", !isCompact && "font-semibold")} />
      {isLinked && isCompact && (
        <ArrowUpRightIcon
          className="text-muted-foreground/70 group-hover:text-primary size-3.5 shrink-0 transition-colors"
          aria-hidden
        />
      )}
    </span>
  );

  const nameWithTooltip = nameTooltip ? (
    <TooltipProvider>
      <Tooltip open={isTooltipOpen || hasKeyboardFocus} onOpenChange={setIsTooltipOpen}>
        <TooltipTrigger asChild>{name}</TooltipTrigger>
        <TooltipContent>{nameTooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : (
    name
  );

  const content = isCompact ? (
    <>
      {avatar}
      {nameWithTooltip}
      {endContent && <span className="flex shrink-0 items-center gap-1">{endContent}</span>}
    </>
  ) : (
    <>
      {avatar}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {nameWithTooltip}
        {showEmail && user.email && <span className="text-muted-foreground truncate text-xs">{user.email}</span>}
      </div>
      {badge && <div className="shrink-0">{badge}</div>}
      {endContent && <div className="flex shrink-0 gap-1">{endContent}</div>}
    </>
  );

  const containerClassName = isCompact
    ? cn(
        "flex max-w-full min-w-0 items-center gap-2",
        isLinked && "group w-fit rounded-sm transition-colors hover:text-primary",
        className,
      )
    : cn(
        "bg-card border-border flex items-center gap-3 rounded-lg border p-3",
        isLinked && "border-primary/30 hover:border-primary/70 hover:bg-primary/5 transition-colors",
        highlighted && "border-primary/20 bg-primary/10 dark:bg-primary/5",
        className,
      );

  const tooltipDescription = nameTooltip ? (
    <span id={tooltipDescriptionId} className="sr-only">
      {nameTooltip}
    </span>
  ) : null;

  if (!isLinked) {
    return (
      <div className={containerClassName} data-testid="user-item">
        {content}
      </div>
    );
  }

  return (
    <Link
      to={getUserDetailPath(user.uuid)}
      className={containerClassName}
      data-testid="user-item"
      aria-describedby={nameTooltip ? tooltipDescriptionId : undefined}
      // The tooltip trigger is the name inside the link, so keyboard focus on the link opens it as well.
      onFocus={(event) => setHasKeyboardFocus(isKeyboardFocus(event.currentTarget))}
      onBlur={() => setHasKeyboardFocus(false)}
    >
      {content}
      {tooltipDescription}
    </Link>
  );
}

function isKeyboardFocus(element: HTMLElement): boolean {
  try {
    return element.matches(":focus-visible");
  } catch {
    return true;
  }
}
